import { XLogo, DiscordLogo } from "@phosphor-icons/react"
import { IconLinkButton } from "@tokenterminal/core/ui/button/IconLinkButton"
import { ActionButton } from "@tokenterminal/ui/ActionButton"
import { type HTMLAttributes, useMemo } from "react"
import { ProjectSocialChannelNameEnum } from "types/ApiTypes"
import { capitalize } from "utils/lodash/capitalize"
import { type ProjectDetails } from "./useProjectDetails"
import Etherscan from "@/icons/etherscan.svg"
import Github from "@/icons/github.svg"
import Governance from "@/icons/governance.svg"
import Website from "@/icons/website.svg"

const icons = {
  twitter: <XLogo width="0.75rem" />,
  github: <Github color="inherit" width="0.75rem" />,
  discord: <DiscordLogo width="0.75rem" />,
  etherscan: <Etherscan color="inherit" width="0.75rem" />,
  website: <Website color="inherit" width="0.75rem" />,
  governance: <Governance color="inherit" width="0.75rem" />,
} as const

type Props = HTMLAttributes<HTMLUListElement> & {
  name: string
  links: ProjectDetails["links"]
}

// Allow people to add new channels without affecting the app right away
const isSupportedLink = (maybeChannel: ProjectSocialChannelNameEnum) =>
  !!ProjectSocialChannelNameEnum[maybeChannel]

// TODO: rework this or at least fix ActionButton

export const ProjectSocialMediaLinks = ({
  links,
  name,
  children,
  ...props
}: Props) => {
  const linksMap = useMemo(() => {
    if (!links?.length) {
      return null
    }

    return links?.reduce(
      (memo, currentVal) => {
        if (memo[currentVal.title as ProjectSocialChannelNameEnum]) {
          return {
            ...memo,
            [currentVal.title]: [
              ...memo[currentVal.title as ProjectSocialChannelNameEnum],
              { title: currentVal.title, url: currentVal.url },
            ],
          }
        }
        return {
          ...memo,
          [currentVal.title]: [currentVal],
        }
      },
      {} as Record<
        ProjectSocialChannelNameEnum,
        { title: ProjectSocialChannelNameEnum; url: string }[]
      >
    )
  }, [links])

  if (!linksMap) {
    return null
  }

  return (
    <ul className="flex flex-row gap-2" {...props}>
      {Object.entries(linksMap).map(([k, linksList]) => {
        const linksComponent = linksList
          ?.filter(({ title }) => {
            if (!isSupportedLink(title)) {
              return false
            }

            return true
          })
          ?.map(({ title, url }) => {
            return {
              label: `${title[0].toUpperCase()}${title.slice(1)}`,
              href: url,
            }
          })

        if (linksComponent?.length > 1) {
          const icon = icons[k as ProjectSocialChannelNameEnum]
          return (
            <ActionButton
              key={k}
              actions={linksComponent}
              color="brandPrimary"
              size="small"
            >
              {icon}
            </ActionButton>
          )
        } else {
          return linksList.map(({ title, url }) =>
            isSupportedLink(title) ? (
              <li key={title}>
                <IconLinkButton
                  icon={() => icons[title]}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="muted"
                  size="small"
                  className="!w-6 !h-6"
                  label={capitalize(title)}
                  aria-label={`View ${name} ${capitalize(title)} link`}
                />
              </li>
            ) : null
          )
        }
      })}
    </ul>
  )
}
